// availability.ts — turn the proxy's whole-product stock map into a per-OPTION
// verdict the option tiles can ask. It resolves the variants an option would force by
// REUSING bom.ts (variantForPiece) + Config.ts (resolveForme), so a tile's grey/block
// verdict can never disagree with what the cart would actually add. Pure, no I/O.
//
// Contextual by design: "Slate" greys only if Slate is out of stock for the pieces in
// the shape currently selected — switch shape and it can come back, mirroring reality.

import type { ProductConfig, ConfigState } from "@/types";
import type { AvailMap } from "@/state/catalog";
import { resolveForme } from "@/state/Config";
import { variantForPiece, resolveBOM } from "@/state/bom";
import { resolveSlug } from "@/state/slug";

export type Axis = "shape" | "size" | "material" | "colorway" | "leg";

function numericId(variantId: string): string {
  const m = /ProductVariant\/(\d+)/.exec(variantId);
  return m ? m[1] : variantId;
}

/** Every Shopify variant a configuration forces (the BOM's pieces + the leg line),
 *  numeric. Mirrors bom.ts grouping so the verdict matches the cart exactly. */
function variantsForState(product: ProductConfig, state: ConfigState): string[] {
  const binding = product.shopify;
  if (!binding || binding.mode !== "shopify-sourced") return [];
  const forme = product.formes.find((f) => f.id === state.formeId);
  if (!forme) return [];
  const ids = new Set<string>();
  // Config-level binding: the whole shape forces ONE variant (mirrors resolveBOM byForme).
  const fb = binding.byForme?.[forme.id];
  if (fb) {
    const v = variantForPiece(fb, state);
    if (v) ids.add(numericId(v));
  } else {
    for (const inst of forme.modules) {
      const mod = product.modules.find((m) => m.id === inst.moduleId);
      const pieceId = mod?.pieceId ?? inst.moduleId;
      const pb = binding.pieces?.find((p) => p.pieceId === pieceId);
      const v = pb ? variantForPiece(pb, state) : undefined;
      if (v) ids.add(numericId(v));
    }
  }
  const legV = binding.legs?.[state.legId];
  if (legV) ids.add(numericId(legV));
  return [...ids];
}

/** The state you'd be in if you picked this option (without committing it). */
function hypotheticalState(
  axis: Axis,
  optionId: string,
  product: ProductConfig,
  state: ConfigState,
): ConfigState {
  const s: ConfigState = { ...state };
  switch (axis) {
    case "colorway":
      s.colorwayId = optionId;
      s.fabricId = optionId;
      break;
    case "material": {
      s.materialId = optionId;
      // Picking a material lands on its first colourway, so probe with that.
      const mat = product.materials?.find((m) => m.id === optionId);
      const first = mat?.colorways[0];
      if (first) {
        s.colorwayId = first.id;
        s.fabricId = first.id;
      }
      break;
    }
    case "leg":
      s.legId = optionId;
      break;
    case "size":
      s.sizeId = optionId;
      s.formeId = resolveForme(product, s.shapeId, optionId).id;
      break;
    case "shape":
      s.shapeId = optionId;
      s.formeId = resolveForme(product, optionId, s.sizeId).id;
      break;
  }
  return s;
}

/**
 * 360-first products (`product.spin`) : an option is only showable when the resulting
 * combination has a GLB, which is also the key of its spin folder and its AR source.
 * Velvet is rendered for 6 Amada shapes so far : on the other shapes its swatches grey out
 * instead of dropping the page onto the hidden 3D engine (Alex, 2026-09-07 : « quand je
 * clique sur velvet le configurateur 360 m'affiche un 3D »). Shapes never grey for this
 * reason : the colour falls back (fallbackVisualState), the shape is always reachable.
 * Products without `spin` are untouched.
 */
export function optionHasVisual(axis: Axis, optionId: string, product: ProductConfig, state: ConfigState): boolean {
  if (!product.spin) return true;
  if (axis === "shape" || axis === "size" || axis === "leg") return true;
  const probe = hypotheticalState(axis, optionId, product, state);
  if (axis === "material") {
    const mat = product.materials?.find((m) => m.id === optionId);
    return !!mat?.colorways.some((c) => resolveSlug(product, { ...probe, colorwayId: c.id, fabricId: c.id }) !== null);
  }
  return resolveSlug(product, probe) !== null;
}

/** The nearest showable combination when the current one has no GLB : same material, first
 *  colourway that exists for this forme ; else the first material that has one ; null when
 *  the current state is already showable or nothing exists for this forme. */
export function fallbackVisualState(product: ProductConfig, state: ConfigState): ConfigState | null {
  if (!product.spin || resolveSlug(product, state) !== null) return null;
  const mats = product.materials ?? [];
  const cur = mats.find((m) => m.id === state.materialId);
  for (const mat of [cur, ...mats.filter((m) => m !== cur)]) {
    if (!mat) continue;
    for (const c of mat.colorways) {
      const s: ConfigState = { ...state, materialId: mat.id, colorwayId: c.id, fabricId: c.id };
      if (resolveSlug(product, s) !== null) return s;
    }
  }
  return null;
}

/**
 * Would selecting this option leave the configuration buyable? An option is OOS only
 * when a variant it FORCES is EXPLICITLY available:false. Unknown / no data / network
 * failure → available (fail-open: never block a sale on a flaky stock read; the real
 * stop is /cart/add.js + checkout).
 */
export function isOptionAvailable(
  axis: Axis,
  optionId: string,
  product: ProductConfig,
  state: ConfigState,
  avail: AvailMap | null,
): boolean {
  if (!optionHasVisual(axis, optionId, product, state)) return false;
  const probe = hypotheticalState(axis, optionId, product, state);
  // Shopify-sourced: a combination the binding can't sell (no per-fabric variant AND no
  // default) is UNAVAILABLE. This is resolved CLIENT-SIDE (no network), so an unmapped
  // colour/material greys instantly and can never be selected into a broken, unbuyable
  // state. Previously an unmapped option fell through to "available" (variantIds == []),
  // which is exactly what let a shopper land on a frozen-price, can't-add-to-cart combo.
  if (product.shopify?.mode === "shopify-sourced") {
    const bom = resolveBOM(product, probe);
    if (!bom.ok && bom.reason === "unmapped") return false;
  }
  if (!avail) return true;
  const variantIds = variantsForState(product, probe);
  if (variantIds.length === 0) return true; // non-shopify option → not stock-gated
  return variantIds.every((id) => avail[id]?.available !== false);
}

/** Does the CURRENT configuration include an out-of-stock variant? Drives the buy
 *  guard (Layer B). True only on an explicit available:false. */
export function configHasOOS(product: ProductConfig, state: ConfigState, avail: AvailMap | null): boolean {
  if (!avail) return false;
  return variantsForState(product, state).some((id) => avail[id]?.available === false);
}
